import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export interface CardProps {
  title: string;
  variant?: 'default' | 'outline' | 'primary' | 'success' | 'danger' | 'warning' | 'info';
  collapsible?: boolean;
  maximizable?: boolean;
  removable?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  id?: string;
}

export function Card({
  title,
  variant = 'default',
  collapsible = false,
  maximizable = false,
  removable = false,
  loading = false,
  children,
  footer,
  className = '',
  id,
}: CardProps) {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const toggleMaximize = useCallback(() => {
    setIsMaximized(prev => !prev);
    // Toggle body class for maximized state
    if (!isMaximized) {
      document.body.classList.add('card-maximized');
    } else {
      document.body.classList.remove('card-maximized');
    }
  }, [isMaximized]);

  const handleRemove = useCallback(() => {
    setIsRemoved(true);
  }, []);

  if (isRemoved) {
    return null;
  }

  const cardClasses = [
    'card',
    variant !== 'default' && `card-${variant}`,
    isCollapsed && 'collapsed-card',
    isMaximized && 'maximized-card',
    loading && 'loading-card',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={cardRef}
      id={id}
      className={cardClasses}
      role="region"
      aria-labelledby={`${id}-title`}
    >
      <div className="card-header">
        <h3 className="card-title" id={`${id}-title`}>{title}</h3>
        <div className="card-tools">
          {collapsible && (
            <button
              type="button"
              className="btn btn-tool"
              onClick={toggleCollapse}
              aria-expanded={!isCollapsed}
              aria-controls={`${id}-body`}
              aria-label={isCollapsed ? t('card.expand', 'Expand card') : t('card.collapse', 'Collapse card')}
            >
              <i className={`bi ${isCollapsed ? 'bi-plus-lg' : 'bi-dash-lg'}`} aria-hidden="true"></i>
            </button>
          )}
          {maximizable && (
            <button
              type="button"
              className="btn btn-tool"
              onClick={toggleMaximize}
              aria-label={isMaximized ? t('card.restore', 'Restore card') : t('card.maximize', 'Maximize card')}
            >
              <i className={`bi ${isMaximized ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`} aria-hidden="true"></i>
            </button>
          )}
          {removable && (
            <button
              type="button"
              className="btn btn-tool"
              onClick={handleRemove}
              aria-label={t('card.remove', 'Remove card')}
            >
              <i className="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          )}
        </div>
      </div>
      {!isCollapsed && (
        <>
          <div className="card-body" id={`${id}-body`}>
            {children}
          </div>
          {footer && (
            <div className="card-footer">
              {footer}
            </div>
          )}
        </>
      )}
    </div>
  );
}
